import { Component, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatStepper } from "@angular/material/stepper";
import { Router } from "@angular/router";
import { AppConstants } from "src/app/constants/constants";
import { Modes } from "src/app/constants/modes";
import { Screener } from "src/app/_models/Screener";
import { PublicCourseService } from "src/app/_services/public-course.service";
import { ScreenerService } from "src/app/_services/screener.service";
import { PublicUserSessionService } from "src/app/_services/public-user-session.service";
import { emailRegex, namesRegex } from "../../shared/form-validators.directive";
import { firstValueFrom, Subject, takeUntil } from "rxjs";
import { Course } from "src/app/_models/Course";
import { convertLevelString } from "src/app/shared/results";
import { setTimeFormat } from "src/app/shared/date-and-time-format";

@Component({
  selector: "app-screener",
  templateUrl: "./screener.component.html",
  styleUrls: ["./screener.component.scss"],
})
export class ScreenerComponent implements OnInit, OnDestroy {
  isLinear = true;
  screenerFormGroup: FormGroup;
  coursesFormGroup: FormGroup;
  courses: any;
  screenerContent: string;
  siteLanguageCode: string;
  isScreenerSaved: boolean = false;
  selectedCourse: string = AppConstants.BLANK;

  @ViewChild("stepper") stepper: MatStepper;
  isSaving: boolean = false;
  destroy$ = new Subject<void>();

  courseLoader: boolean = false;

  constructor(
    private _formBuilder: FormBuilder,
    private courseService: PublicCourseService,
    private userSessionService: PublicUserSessionService,
    private screenerService: ScreenerService,
    private router: Router,
    @Inject(LOCALE_ID) protected localeId: string
  ) {
    this.siteLanguageCode = localeId;
  }

  ngOnInit(): void {
    this.coursesFormGroup = this._formBuilder.group({
      courses: ["", Validators.required],
    });

    this.screenerFormGroup = this._formBuilder.group({
      firstName: [
        null, 
        [ Validators.required, 
          Validators.pattern(namesRegex), 
          Validators.minLength(2), 
          Validators.maxLength(64)]
      ],
      lastName: [
        null, 
        [ Validators.required, 
          Validators.pattern(namesRegex), 
          Validators.minLength(2), 
          Validators.maxLength(64)]
      ],
      email: [
        null, 
        [ Validators.required, 
          Validators.pattern(emailRegex), 
          Validators.minLength(2), 
          Validators.maxLength(100)]
      ],
    });

    //checks if sessionStorageHasValue
    if (this.sessionStorageHasValue()) {
      this.screenerFormGroup.controls[AppConstants.FIRST_NAME].setValue(
        sessionStorage.getItem(AppConstants.FIRST_NAME)
      );
      this.screenerFormGroup.controls[AppConstants.LAST_NAME].setValue(
        sessionStorage.getItem(AppConstants.LAST_NAME)
      );
      this.screenerFormGroup.controls[AppConstants.EMAIL].setValue(
        sessionStorage.getItem(AppConstants.EMAIL)
      );
    }
  }

  ngAfterViewInit() {
    if (this.sessionStorageHasValue()) {
      this.stepper.selectedIndex = 1;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();  
  }

  get firstName() {
    return this.screenerFormGroup.get(AppConstants.FIRST_NAME);
  }

  get lastName() {
    return this.screenerFormGroup.get(AppConstants.LAST_NAME);
  }

  get email() {
    return this.screenerFormGroup.get(AppConstants.EMAIL);
  }

  /**This checks if there is already a screener detail captured on the session
   * AND if the screener details are dirty.
   *
   * IF the screener details saved on the session is the same with the current state
   * THEN it will just redirect to SCreener content
   *
   * ELSE
   * It will prompt to save a new screener record both the browser session and to DB
   * AND will then redirect to Screener content
   */
  public saveScreenerDetails(objectId: string) {
    if (
      this.sessionStorageHasValue() &&
      this.firstName.value == sessionStorage.getItem(AppConstants.FIRST_NAME) &&
      this.lastName.value == sessionStorage.getItem(AppConstants.LAST_NAME) &&
      this.email.value == sessionStorage.getItem(AppConstants.EMAIL) &&
      sessionStorage.getItem(AppConstants.SCREENER_ID) != undefined
    ) {
      this.routeToContentPlayer(objectId);
      return;
    }

    sessionStorage.setItem(AppConstants.FIRST_NAME, this.firstName.value);
    sessionStorage.setItem(AppConstants.LAST_NAME, this.lastName.value);
    sessionStorage.setItem(AppConstants.EMAIL, this.email.value);

    let screener = new Screener();
    screener.firstName = this.firstName.value;
    screener.lastName = this.lastName.value;
    screener.email = this.email.value;
    screener.dateCreated = new Date();

    this.selectedCourse = objectId;
    this.isSaving = !this.isSaving;
    this.screenerService.addScreener$(screener).subscribe(
      (resp) => {
        this.isScreenerSaved = !this.isScreenerSaved;
        sessionStorage.setItem(AppConstants.SCREENER_ID, String(resp.id));
        this.isSaving = !this.isSaving;
        this.routeToContentPlayer(objectId);
      },
      () => {
        this.isScreenerSaved = false;
        console.error("Error adding screener record. Contact administrator.");
      }
    );
  }

  public getCourseByEmail(): void {
    this.courseLoader = true;
    this.courseService
      .getCourseByEmail$(this.siteLanguageCode, this.email.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe((courses: Course[]) => {
        courses = courses.filter(c => c.inUsed &&
          c.metadatas !== null
        ); 
        courses.forEach((course) => {
          course[AppConstants.SCREENER_OBJ_ID] = course.metadatas.find(
            (m) => m.name == AppConstants.SCREENER
          ).value;

          if(course.screenerResult !== null) {
            course.screenerResult.overallResult = convertLevelString(course.screenerResult.overallResult);
            course.screenerResult.totalTime = setTimeFormat(course.screenerResult.totalTime, this.siteLanguageCode)
          }
        });
        this.courses = courses;
        this.courseLoader = false;
      })
  }
 
  public async routeToContentPlayer(objectId: string): Promise<void> {
    await firstValueFrom(this.userSessionService.setIsAllowAccessContent$(AppConstants.TRUE));
      this.router.navigate([`/contentplayer/${objectId}`], {
        queryParams: { mode: Modes.Screener },
      });
  }

  public sessionStorageHasValue(): boolean {
    return Boolean(
      sessionStorage.getItem(AppConstants.FIRST_NAME) != null &&
      sessionStorage.getItem(AppConstants.LAST_NAME) != null &&
      sessionStorage.getItem(AppConstants.EMAIL)
    );
  }
}
